import { createApiService } from './api';
import type {
  OnboardingSession,
  StartOnboardingPayload,
  Survey,
  SurveyResponseRecord,
  TermsAcceptanceUpdateRequest,
  UserConsentUpdateRequest,
  GdprComplianceUpdateRequest,
  PreferencesUpdateRequest,
  UserOnboardingRequest,
} from '@/types';
import type { SubscriptionPlan } from '@/types/subscription';

// User Profile Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const USER_PROFILE_BASE_URL =
  import.meta.env.VITE_FIVE_USER_PROFILE_SERVICE_URL ||
  import.meta.env.FIVE_USER_PROFILE_SERVICE_URL ||
  '/api/user-profile';

export const DEFAULT_ONBOARDING_SURVEY_ID = 'SVY-five_onboarding_v1';

// Onboarding is a public flow; do NOT send stored auth tokens by default.
const onboardingClient = createApiService(USER_PROFILE_BASE_URL);

export const onboardingService = {
  fetchSurvey: (surveyId = DEFAULT_ONBOARDING_SURVEY_ID) =>
    onboardingClient.get<Survey>(
      `/api/v1/onboarding/start/survey/${surveyId}`,
      {
        skipAuth: true,
      }
    ),
  /** Create or resolve onboarding session. When useAuth is true, sends Bearer token so backend can return session for logged-in user (e.g. after login to subscription). */
  startSession: (payload: StartOnboardingPayload, options?: { useAuth?: boolean }) => {
    const useAuth = options?.useAuth === true;
    return onboardingClient.post<OnboardingSession>('/api/v1/onboarding/start', payload, {
      skipAuth: !useAuth,
    });
  },
  submitResponses: (sessionId: string, responses: SurveyResponseRecord[]) => {
    return onboardingClient.post<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/survey-responses`,
      responses,
      { skipAuth: true }
    );
  },
  getSession: (sessionId: string) => {
    return onboardingClient.get<OnboardingSession>(`/api/v1/onboarding/${sessionId}`, {
      skipAuth: true,
    });
  },
  getSessionDetails: (sessionId: string) => {
    return onboardingClient.get<OnboardingSession & { surveyResponses?: SurveyResponseRecord[] }>(
      `/api/v1/onboarding/${sessionId}/details`,
      { skipAuth: true }
    );
  },
  getSessionByDevice: (deviceId: string) =>
    onboardingClient.get<OnboardingSession>(
      `/api/v1/onboarding/device/${deviceId}`,
      {
        skipAuth: true,
      }
    ),
  /** Optional: update session location. Non-breaking; failures are handled by the caller. No retries. */
  updateLocation: (
    sessionId: string,
    location: Record<string, unknown>
  ) => {
    return onboardingClient.put<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/location`,
      location,
      { skipAuth: true, skipRetry: true }
    );
  },
  updateDevice: (
    sessionId: string,
    deviceInfo: Record<string, unknown>
  ) =>
    onboardingClient.put<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/device`,
      deviceInfo,
      { skipAuth: true }
    ),
  // Step 5: Consent endpoints (all use PATCH)
  acceptTerms: (sessionId: string, termsData: TermsAcceptanceUpdateRequest) => {
    return onboardingClient.patch<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/terms-acceptance`,
      termsData,
      { skipAuth: true }
    );
  },
  submitUserConsent: (sessionId: string, consentData: UserConsentUpdateRequest) => {
    return onboardingClient.patch<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/user-consent`,
      consentData,
      { skipAuth: true }
    );
  },
  updatePreferences: (
    sessionId: string,
    preferences: PreferencesUpdateRequest
  ) => {
    return onboardingClient.patch<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/preferences`,
      preferences,
      { skipAuth: true }
    );
  },
  submitGDPRCompliance: (sessionId: string, gdprData: GdprComplianceUpdateRequest) => {
    return onboardingClient.patch<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/gdpr-compliance`,
      gdprData,
      { skipAuth: true }
    );
  },
  updateConsentData: (
    sessionId: string,
    consentData: {
      termsAccepted?: boolean;
      termsAcceptedAt?: string;
      privacyPolicyAccepted?: boolean;
      privacyPolicyAcceptedAt?: string;
      marketingConsent?: boolean;
      marketingConsentAt?: string;
      dataProcessingConsent?: boolean;
      dataProcessingConsentAt?: string;
      analyticsConsent?: boolean;
      analyticsConsentAt?: string;
      termsVersion?: string;
      privacyPolicyVersion?: string;
    }
  ) => {
    return onboardingClient.patch<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/consent-data`,
      consentData,
      { skipAuth: true }
    );
  },
  // Additional endpoints
  updateSubscription: (
    sessionId: string,
    subscriptionData: {
      planCode?: string;
      billingEmail?: string;
      billingCycle?: string;
      currency?: string;
      countryCode?: string;
      paymentGateway?: string;
      // Allow forwards-compatible extra fields
      [key: string]: unknown;
    }
  ) => {
    return onboardingClient.patch<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/subscription`,
      subscriptionData,
      { skipAuth: true }
    );
  },
  updateDevicePartial: (sessionId: string, deviceData: Record<string, unknown>) =>
    onboardingClient.patch<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/device-partial`,
      deviceData,
      { skipAuth: true }
    ),
  // Step 6: Complete onboarding
  complete: (sessionId: string, userData: UserOnboardingRequest) => {
    return onboardingClient.post<OnboardingSession>(
      `/api/v1/onboarding/${sessionId}/complete`,
      userData,
      {
        skipAuth: true,
      }
    );
  },
  abandon: (sessionId: string) =>
    onboardingClient.post<void>(
      `/api/v1/onboarding/${sessionId}/abandon`,
      undefined,
      { skipAuth: true }
    ),
  // Step 8: User profile endpoints (poll after completion)
  // These endpoints require authentication (user must be logged in after OAuth)
  getUserProfile: (userId: string) => {
    return onboardingClient.get<Record<string, unknown>>(
      `/api/v1/profiles/${userId}`
    );
  },
  getUserBio: (userId: string) => {
    // Bio is part of the full profile response
    return onboardingClient.get<Record<string, unknown>>(
      `/api/v1/profiles/${userId}`
    );
  },
  getUserLearningPath: (userId: string) => {
    // Learning path is part of the full profile response
    return onboardingClient.get<Record<string, unknown>>(
      `/api/v1/profiles/${userId}`
    );
  },
  // Get subscription plans for onboarding
  getSubscriptionPlans: (_skipAuth?: boolean) => {
    return onboardingClient.get<SubscriptionPlan[]>(
      '/api/v1/onboarding/start/subscription-plans',
      {
        skipAuth: true,
      }
    );
  },
};

